import { useMemo } from 'react';

export interface BreadcrumbItem {
  id: string;
  name: string;
  path: string;
}

export interface DataSource {
  id?: number | string;
  name?: string;
  [key: string]: any;
}

export interface DataSources {
  cdnSource?: DataSource | null;
  wafSource?: DataSource | null;
  workspaceSource?: DataSource | null;
  [key: string]: DataSource | null | undefined;
}

/**
 * Enhanced breadcrumb hook that generates breadcrumb items from URL pathname
 * with context-aware naming and proper path generation.
 *
 * @param pathname - Current URL pathname
 * @param dataSources - Object containing various data sources (cdn, waf, workspace)
 * @param translate - Translation function to get localized segment names
 * @returns Array of breadcrumb items with ID, name, and path
 */
export const useBreadcrumbItems = (
  pathname: string,
  dataSources: DataSources,
  translate: (key: string) => string,
): BreadcrumbItem[] => {
  return useMemo(() => {
    if (pathname.includes('invite')) {
      return [
        {
          id: 'invite',
          name: translate('navigator.workspaceInvitation'),
          path: pathname,
        },
      ];
    }

    const segments = pathname.split('/').filter(Boolean);
    const result: BreadcrumbItem[] = [];
    let currentPath = '';

    segments.forEach((segment, index) => {
      if (segment === 'configuration') {
        currentPath += `/${segment}`;
        return;
      }

      currentPath += `/${segment}`;
      const prevSegment = index > 0 ? segments[index - 1] : '';
      const isNumeric = /^\d+$/.test(segment);

      const relevantDataSource = (() => {
        if (prevSegment === 'cdn' && dataSources.cdnSource) {
          return dataSources.cdnSource;
        } else if (prevSegment === 'waf' && dataSources.wafSource) {
          return dataSources.wafSource;
        } else if (pathname.includes('workspace') && dataSources.workspaceSource) {
          return dataSources.workspaceSource;
        }
        return null;
      })();

      if (isNumeric) {
        handleNumericSegment(segment, prevSegment, relevantDataSource, result, currentPath, dataSources);
      } else {
        handleRouteSegment(segment, result, currentPath, translate);
      }
    });

    return result;
  }, [pathname, dataSources, translate]);
};

/**
 * Helper function to handle numeric segments (IDs)
 */
function handleNumericSegment(
  segment: string,
  prevSegment: string,
  relevantDataSource: DataSource | null,
  result: BreadcrumbItem[],
  currentPath: string,
  dataSources: DataSources,
): void {
  if (prevSegment === 'cdn' && relevantDataSource && String(relevantDataSource.id) === segment) {
    result.push({
      id: segment,
      name: relevantDataSource.name ?? `CDN ${segment}`,
      path: currentPath,
    });
  } else if (prevSegment === 'waf' && dataSources.wafSource) {
    result.push({
      id: segment,
      name: dataSources.wafSource.name ?? `WAF ${segment}`,
      path: currentPath,
    });
  } else if (relevantDataSource && String(relevantDataSource.id) === segment) {
    result.push({
      id: segment,
      name: relevantDataSource.name ?? `ID: ${segment}`,
      path: currentPath,
    });
  } else {
    const contextPrefix = getContextPrefix(prevSegment);
    result.push({
      id: segment,
      name: contextPrefix ? `${contextPrefix} ${segment}` : `ID: ${segment}`,
      path: currentPath,
    });
  }
}

/**
 * Helper function to handle route segments (non-numeric)
 */
function handleRouteSegment(
  segment: string,
  result: BreadcrumbItem[],
  currentPath: string,
  translate: (key: string) => string,
): void {
  const translationKey = `navigator.${segment}`;
  const translatedName = translate(translationKey);

  result.push({
    id: segment,
    name: translatedName !== translationKey ? translatedName : formatSegmentName(segment),
    path: currentPath,
  });
}

/**
 * Format segment name for display
 */
function formatSegmentName(segment: string): string {
  // Handle kebab-case, snake_case or camelCase segment names
  return segment
    .replace(/[-_]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Get context prefix for an ID based on preceding segment
 */
function getContextPrefix(segment: string): string {
  if (!segment) return '';

  // Map known segment types to their display names
  const contextMap: Record<string, string> = {
    cdn: 'CDN',
    waf: 'WAF',
    rule: 'Rule',
    user: 'User',
    zone: 'Zone',
    policy: 'Policy',
    workspace: 'Workspace',
    cluster: 'Cluster',
  };

  return contextMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
}
